import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  InputAdornment,
} from '@mui/material';
import { jids } from '../../../../../data/forms/generalDropdownOptions.js';
import { terminalTypeOptions } from '../../../../../data/forms/nrc/californiaPtlDropdownOptions.js';
import RetailerLookupButton from '../retailerLookupButton.js';
import UpdateStoreHoursModal from '../updateStoreHoursModal.js';
import axios from 'axios';

const CaliforniaPtlForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: 'California',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    openCase: '',
    caseNumber: '',
    updateStoreHours: '',
    updatedHours: {
      sundayOpen: '',
      sundayClose: '',
      mondayOpen: '',
      mondayClose: '',
      tuesdayOpen: '',
      tuesdayClose: '',
      wednesdayOpen: '',
      wednesdayClose: '',
      thursdayOpen: '',
      thursdayClose: '',
      fridayOpen: '',
      fridayClose: '',
      saturdayOpen: '',
      saturdayClose: '',
    },
    updatePhone: '',
    newPhone: '',
    terminalType: '',
    keyRequest: '',
    messageToSite: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateStoreHoursModalOpen, setIsUpdateStoreHoursModalOpen] =
    useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'updateStoreHours' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        updatedHours: {
          sunday: { open: '', close: '' },
          monday: { open: '', close: '' },
          tuesday: { open: '', close: '' },
          wednesday: { open: '', close: '' },
          thursday: { open: '', close: '' },
          friday: { open: '', close: '' },
          saturday: { open: '', close: '' },
        },
      }));
    }

    if (name === 'openCase' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        caseNumber: '',
      }));
    }

    if (name === 'updatePhone' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        newPhone: '',
      }));
    }
  };

  const handleUpdateStoreHoursModalOpenClose = () => {
    setIsUpdateStoreHoursModalOpen(!isUpdateStoreHoursModalOpen);
  };

  const handleClear = () => {
    setForm({
      ...form,
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      openCase: '',
      caseNumber: '',
      updateStoreHours: '',
      updatedHours: {
        sundayOpen: '',
        sundayClose: '',
        mondayOpen: '',
        mondayClose: '',
        tuesdayOpen: '',
        tuesdayClose: '',
        wednesdayOpen: '',
        wednesdayClose: '',
        thursdayOpen: '',
        thursdayClose: '',
        fridayOpen: '',
        fridayClose: '',
        saturdayOpen: '',
        saturdayClose: '',
      },
      updatePhone: '',
      newPhone: '',
      terminalType: '',
      keyRequest: '',
      messageToSite: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/californiaPTL/submit`,
        data: {
          ...form,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      updateStoreHours,
      updatePhone,
      openCase,
      jurisdiction,
      retailerId,
      retailerName,
      address,
      phoneNumber,
      caseNumber,
      newPhone,
    } = form;

    if (
      !updateStoreHours ||
      !updatePhone ||
      !openCase ||
      !jurisdiction ||
      !retailerId ||
      !retailerName ||
      !address ||
      !phoneNumber
    ) {
      return false;
    }

    if (openCase === 'Yes' && !caseNumber) {
      return false;
    }

    if (updatePhone === 'Yes' && !newPhone) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid
          size={12}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <FormControl required>
            <FormLabel sx={{ fontFamily: 'BrightstarSans-Bold' }}>
              Update Store Hours?
            </FormLabel>
            <RadioGroup
              name='updateStoreHours'
              value={form.updateStoreHours}
              onChange={handleChange}
              row
            >
              <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='No' control={<Radio />} label='No' />
            </RadioGroup>
            <Tooltip
              title={
                form.updateStoreHours !== 'Yes'
                  ? "Please select 'Yes' on Update Store Hours to enable the Update button"
                  : ''
              }
            >
              <Box>
                <Button
                  variant='contained'
                  disabled={form.updateStoreHours !== 'Yes'}
                  sx={{
                    backgroundColor: 'var(--procyon-green)',
                    color: '#000000',
                    fontFamily: 'BrightstarSans-Regular',
                  }}
                  onClick={handleUpdateStoreHoursModalOpenClose}
                >
                  Update
                </Button>
              </Box>
            </Tooltip>
          </FormControl>
          <FormControl required>
            <FormLabel sx={{ fontFamily: 'BrightstarSans-Bold' }}>
              Update Phone?
            </FormLabel>
            <RadioGroup
              name='updatePhone'
              value={form.updatePhone}
              onChange={handleChange}
              row
            >
              <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='No' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          <FormControl required>
            <FormLabel sx={{ fontFamily: 'BrightstarSans-Bold' }}>
              Open Case?
            </FormLabel>
            <RadioGroup
              name='openCase'
              value={form.openCase}
              onChange={handleChange}
              row
            >
              <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='No' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            required
            disabled
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>
              {translations.jurisdiction || 'Jurisdiction'}
            </InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Jurisdiction'}
              onChange={handleChange}
              name='jurisdiction'
            >
              {jids.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Retailer Name'
            name='retailerName'
            required
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
            label='Phone Number'
            name='phoneNumber'
            required
          />
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Terminal Type'}</InputLabel>
            <Select
              value={form.terminalType}
              label={'Terminal Type'}
              onChange={handleChange}
              name='terminalType'
            >
              {terminalTypeOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={'Serial Number'} arrow>
            <TextField
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
              value={form.keyRequest}
              onChange={handleChange}
              label='Key Request'
              name='keyRequest'
            />
          </Tooltip>
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Tooltip
            title={'Enter a Retailer ID (numeric values only) to lookup'}
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <Tooltip
            title={
              form.updatePhone !== 'Yes'
                ? "Please select 'Yes' on Update Phone to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              disabled={form.updatePhone !== 'Yes'}
              required={form.updatePhone === 'Yes'}
              label='New Phone Number'
              name='newPhone'
              fullWidth
              value={form.newPhone}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.openCase !== 'Yes'
                ? "Please select 'Yes' on Open Case to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              disabled={form.openCase !== 'Yes'}
              required={form.openCase === 'Yes'}
              id='outlined-required'
              label='Case Number'
              name='caseNumber'
              fullWidth
              value={form.caseNumber}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <Tooltip
            title={
              'Be sure to insert in all case notes and if needed please indicate: Store Add/Name Update, Top 200, Comm/Supervisor Escalation, Relocation, etc.'
            }
            arrow
          >
            <TextField
              label='Message to Site'
              name='messageToSite'
              value={form.messageToSite}
              onChange={handleChange}
              multiline
              rows={4.1}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      {isUpdateStoreHoursModalOpen && (
        <UpdateStoreHoursModal
          isOpen={isUpdateStoreHoursModalOpen}
          handleClose={handleUpdateStoreHoursModalOpenClose}
          updatedHours={form.updatedHours}
          setForm={setForm}
        />
      )}
    </>
  );
};

export default CaliforniaPtlForm;
